import { Icon, Loader } from 'semantic-ui-react';
import { useGetEarningsSummaryQuery } from '../../redux/apis/endpoints/earningsApi';

import PayoutSettings from './PayoutSettings';
import './EarningsDashboard.css';

/**
 * Format a timestamp into a human-readable relative time string.
 * @param {string} timestamp - ISO date string
 * @returns {string} Relative time (e.g., "2h ago", "3d ago")
 */
const formatRelativeTime = (timestamp) => {
  if (!timestamp) return '';
  const now = Date.now();
  const then = new Date(timestamp).getTime();
  const diffMs = now - then;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHr = Math.floor(diffMs / 3600000);
  const diffDay = Math.floor(diffMs / 86400000);

  if (diffMin < 1) return 'just now';
  if (diffMin < 60) return `${diffMin}m ago`;
  if (diffHr < 24) return `${diffHr}h ago`;
  if (diffDay < 30) return `${diffDay}d ago`;
  return new Date(timestamp).toLocaleDateString();
};

/**
 * EarningsDashboard - Shows the user's earnings from sharing closet items.
 * Displays summary stat cards, recent earnings feed, and provides access
 * to the full earnings history.
 *
 * Part of the SUSU Closet Growth Engine feature.
 */
const EarningsDashboard = () => {
  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError
  } = useGetEarningsSummaryQuery();

  // --- Loading state ---
  if (summaryLoading) {
    return (
      <div className="earnings-dashboard">
        <div className="earnings-header">
          <span className="earnings-header-title">My Earnings</span>
        </div>
        <div className="earnings-loading">
          <Loader active inline="centered" size="small" />
        </div>
      </div>
    );
  }

  // --- Error state ---
  if (summaryError || !summary) {
    return (
      <div className="earnings-dashboard">
        <div className="earnings-header">
          <span className="earnings-header-title">My Earnings</span>
        </div>
        <div className="earnings-empty">
          <Icon name="exclamation triangle" className="earnings-empty-icon" />
          <span className="earnings-empty-title">Could not load earnings</span>
          <span className="earnings-empty-text">
            Please try again later.
          </span>
        </div>
      </div>
    );
  }

  const {
    availableUsd = 0,
    paidOutUsd = 0,
    lifetimeUsd = 0,
    payoutPerTryOnUsd = 0,
    totalItemsShared,
    totalTryOnsReceived,
    recentEarnings = []
  } = summary;

  const hasEarnings = recentEarnings.length > 0 || lifetimeUsd > 0 || availableUsd > 0;
  const money = (n) => `$${Number(n || 0).toFixed(2)}`;

  // One feed, scrolled rather than paged — a "view all" that reveals a list
  // you already have is a click for nothing.
  const displayList = recentEarnings;

  return (
    <div className="earnings-dashboard">
      {/* Header with info tooltip */}
      <div className="earnings-header">
        <span className="earnings-header-title">My Earnings</span>
        <div className="earnings-info-tooltip">
          <button className="earnings-info-trigger" type="button" aria-label="How earnings work">
            ?
          </button>
          <div className="earnings-info-content">
            Share items from your SUSU Closet. Every time another member tries one on, you earn {money(payoutPerTryOnUsd)}. Earnings are paid out to your connected account once you turn on monthly payouts.
          </div>
        </div>
      </div>

      {/* Stats cards */}
      <div className="earnings-stats-row">
        <div className="earnings-stat-card primary">
          <div className="earnings-stat-value teal">{money(lifetimeUsd || availableUsd)}</div>
          <div className="earnings-stat-label">
            {paidOutUsd > 0 ? `Earned · ${money(paidOutUsd)} paid out` : 'Earned'}
          </div>
        </div>
        <div className="earnings-stat-card">
          <div className="earnings-stat-value dark">{totalItemsShared}</div>
          <div className="earnings-stat-label">Items Shared</div>
        </div>
        <div className="earnings-stat-card">
          <div className="earnings-stat-value dark">{totalTryOnsReceived}</div>
          <div className="earnings-stat-label">Try-Ons Received</div>
        </div>
      </div>

      {/* Recent earnings or empty state */}
      {hasEarnings ? (
        <div className="earnings-recent-section">
          <div className="earnings-recent-header">
            <span className="earnings-recent-title">Activity</span>
          </div>

          <div className="earnings-list">
            {displayList.map((earning, index) => (
                <div className="earnings-row" key={earning.id || index}>
                  <div className="earnings-row-left">
                    <div className="earnings-row-text">
                      {earning.type === 'payout' ? (
                        <span className="earnings-row-item-name">Paid out to your account</span>
                      ) : (
                        <>
                          <span className="earnings-row-username">
                            @{earning.consumerUsername || earning.consumer?.username || earning.username || 'member'}
                          </span>
                          {' tried your '}
                          <span className="earnings-row-item-name">
                            {earning.clothesName || earning.closetClothes?.clothesName || earning.itemName || 'item'}
                          </span>
                        </>
                      )}
                    </div>
                    <div className="earnings-row-time">
                      {formatRelativeTime(earning.createdAt || earning.created_at || earning.timestamp)}
                    </div>
                  </div>
                  <div className="earnings-row-right">
                    <span className={`earnings-credit-badge ${earning.type === 'payout' ? 'payout' : ''}`}>
                      {earning.amountUsd < 0 ? '' : '+'}{money(earning.amountUsd)}
                    </span>
                  </div>
                </div>
              ))}
          </div>
        </div>
      ) : (
        <div className="earnings-empty">
          <Icon name="share alternate" className="earnings-empty-icon" />
          <span className="earnings-empty-title">No earnings yet</span>
          <span className="earnings-empty-text">
            Share items from your closet to start earning when others try them on!
          </span>
        </div>
      )}
      <PayoutSettings />
    </div>
  );
};

export default EarningsDashboard;
